<template>
  <div class="wrapper wrapper-content animated fadeInUp">
    <div class="row">
      <div class="col-lg-12">
        <div class="ibox">
          <div class="ibox-title">
            <h2>Allegro - Moje konta</h2>
            <ol class="breadcrumb">
              <li><router-link to="/">Home</router-link></li>
              <li class="active">
                <strong>Allegro - Moje konta</strong>
              </li>
            </ol>
            <div class="ibox-tools">
              <router-link v-if="canAddAccount" to="/allegro/accounts/create">
                <button class="btn btn-primary">
                  <i class="fa fa-plus"></i> Dodaj konto
                </button>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row" v-if="!loading">
      <div class="col-lg-4" v-for="account in items" :key="account.pk">
        <div class="ibox">
          <div class="ibox-title">
            <h5><i class="fa fa-store"></i> {{ account.name }}</h5>
            <div class="ibox-tools">
              <span
                v-if="
                  !account?.configuration?.device_flow &&
                  !account?.configuration?.access_token
                "
                class="label label-warning"
              >
                <i class="fa fa-exclamation-triangle"></i>
                <span>Wymaga połączenia</span>
              </span>
              <span
                v-else-if="
                  account?.configuration?.device_flow &&
                  account.loadingAccessToken
                "
                class="label label-success"
              >
                <i class="fa fa-spinner fa-spin"></i>
                <span>W trakcie połączenia</span>
              </span>
              <span
                v-else-if="account?.configuration?.access_token"
                class="label label-primary"
              >
                <i class="fa fa-check-circle"></i>
                <span>Połączone</span>
              </span>
              <span v-else class="label label-success">
                <span>W trakcie połączenia</span>
              </span>
            </div>
          </div>
          <div class="ibox-content">
            <div class="text-muted m-b-sm" v-if="account.email">
              <i class="fa fa-user"></i> {{ account.email }}
            </div>
            <div class="row m-t-sm allegro-account-stats">
              <div class="col-xs-4 text-center allegro-account-stat">
                <h2 class="no-margins">
                  {{ account?.stats?.active_offers || 0 }}
                </h2>
                <small>Oferty</small>
              </div>
              <div class="col-xs-4 text-center allegro-account-stat">
                <h2 class="no-margins">
                  {{ account?.stats?.placed_orders || 0 }}
                </h2>
                <small>Zamówienia</small>
              </div>
              <div class="col-xs-4 text-center allegro-account-stat">
                <h2 class="no-margins">
                  {{ account?.stats?.unread_threads || 0 }}
                </h2>
                <small>Wiadomości</small>
              </div>
            </div>

            <div
              v-if="account?.configuration?.device_flow"
              class="m-t-md text-muted"
            >
              <small>
                <i class="fa fa-clock-o"></i>
                Kod urządzenia:
                {{ account.configuration.device_flow.expires_at }}
              </small>
            </div>
            <div
              v-if="account?.configuration?.access_token"
              class="m-t-md text-muted"
            >
              <small>
                <i class="fa fa-clock-o"></i>
                Token dostępu ważny:
                {{ account.configuration.access_token.expires_at }}
              </small>
            </div>
          </div>
          <div class="ibox-footer allegro-account-actions">
            <router-link :to="`/allegro/accounts/${account.pk}/overview`">
              <button class="btn btn-default btn-block allegro-account-action">
                <i class="fa fa-eye"></i> Podgląd konta
              </button>
            </router-link>
            <router-link
              v-if="canViewAnySettings"
              :to="`/allegro/accounts/${account.pk}/settings`"
            >
              <button class="btn btn-default btn-block allegro-account-action">
                <i class="fa fa-cog"></i> Ustawienia
                <span
                  v-if="hasMissingSettings(account)"
                  class="label label-warning"
                >
                  <i class="fa fa-exclamation"></i>
                </span>
              </button>
            </router-link>
            <button
              v-if="
                canAssociateAccount &&
                !account?.configuration?.device_flow &&
                !account?.configuration?.access_token
              "
              :disabled="account.loadingDeviceCode"
              class="btn btn-warning btn-block allegro-account-action allegro-account-connect"
              @click="getDeviceFlow(account)"
            >
              <i
                :class="
                  account.loadingDeviceCode
                    ? 'fa fa-spinner fa-spin'
                    : 'fa fa-link'
                "
              ></i>
              {{
                account.loadingDeviceCode
                  ? "Generowanie kodu..."
                  : "Połącz z Allegro"
              }}
            </button>
            <a
              v-if="canAssociateAccount && account?.configuration?.device_flow"
              class="btn btn-success btn-block allegro-account-action"
              :href="
                account.configuration.device_flow.verification_uri_complete
              "
              target="_blank"
            >
              <i class="fa fa-external-link"></i> Zatwierdź dostęp w Allegro
            </a>
            <button
              v-if="
                canRevokeAccountToken && account?.configuration?.access_token
              "
              :disabled="account.loadingRevoke"
              class="btn btn-danger btn-block allegro-account-action"
              @click="openRevokeModal(account)"
            >
              <i
                :class="
                  account.loadingRevoke
                    ? 'fa fa-spinner fa-spin'
                    : 'fa fa-unlink'
                "
              ></i>
              {{ account.loadingRevoke ? "Rozłączanie..." : "Rozłącz konto" }}
            </button>
          </div>
        </div>
      </div>
      <div class="col-lg-12" v-if="items.length === 0">
        <div class="ibox animated fadeInDown">
          <div class="ibox-content text-center">
            <div class="text-center">
              <i
                class="fa fa-store-slash"
                style="font-size: 100px; color: #ddd"
              ></i>
              <h3 class="font-bold">
                Nie masz jeszcze dodanych w systemie kont Allegro
              </h3>
              <div class="error-desc">
                Dodaj pierwsze konto i skonfiguruj połączenie, aby rozpocząć
                sprzedaż.
                <br />
                <router-link v-if="canAddAccount" to="/allegro/accounts/create">
                  <button class="btn btn-primary m-t">
                    <i class="fa fa-plus"></i> Dodaj konto
                  </button>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight" v-if="loading">
    <div class="spiner-example">
      <div class="sk-spinner sk-spinner-wave">
        <div class="sk-rect1"></div>
        <div class="sk-rect2"></div>
        <div class="sk-rect3"></div>
        <div class="sk-rect4"></div>
        <div class="sk-rect5"></div>
      </div>
    </div>
  </div>

  <Modal
    v-if="accountToRevoke"
    title="Rozłączenie konta"
    :content="revokeModalContent"
    @callbackOk="revokeAccessToken"
    @callbackClose="accountToRevoke = null"
  />
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import Modal from "../caffeprint/Modal.vue";
import { hasPermission } from "../../permissions";
import { useAllegroConnection } from "../../composables/useAllegroConnection";
import {
  allegroAccountPermissionsConst,
  allegroConfigPermissionsConst,
  allegroSellerPermissionsConst,
  allegroSenderPermissionsConst,
  allegroCodReceiverPermissionsConst,
  allegroResponsibleProducerPermissionsConst,
  allegroSafetyInformationPermissionsConst,
} from "../../constants/allegroConstants";

export default {
  name: "AllegroAccountsList",
  components: { Modal },
  setup() {
    const store = useStore();
    const loading = ref(true);
    const items = ref([]);

    // --- Current admin permissions ---
    const canAddAccount = computed(() =>
      hasPermission(allegroAccountPermissionsConst.add)
    );
    const canViewAnySettings = computed(
      () =>
        hasPermission(allegroConfigPermissionsConst.view) ||
        hasPermission(allegroSellerPermissionsConst.view) ||
        hasPermission(allegroSenderPermissionsConst.view) ||
        hasPermission(allegroCodReceiverPermissionsConst.view) ||
        hasPermission(allegroResponsibleProducerPermissionsConst.view) ||
        hasPermission(allegroSafetyInformationPermissionsConst.view)
    );
    // --- End: current admin permissions ---

    const accountsData = computed(() => store.getters.getAllegroAccountsData);

    const getData = () => {
      loading.value = true;
      store.dispatch("getAllegroAccounts", {});
    };

    // --- Connecting the account to Allegro (device flow) ---
    const {
      accountToRevoke,
      revokeModalContent,
      canAssociateAccount,
      canRevokeAccountToken,
      refreshAuthInfo,
      getDeviceFlow,
      openRevokeModal,
      revokeAccessToken,
    } = useAllegroConnection({
      resolveAccount: (configurationPk) =>
        items.value.find((item) => item.configuration?.pk === configurationPk),
      // Account stats change once the connection state changes
      onChange: () => getData(),
    });
    // --- End: connecting the account to Allegro ---

    const hasMissingSettings = (account) => {
      if (!account?.settings_fill_statuses) {
        return false;
      }
      return Object.values(account.settings_fill_statuses).some(
        (status) => status === false
      );
    };

    watch(
      () => accountsData.value,
      (newValue) => {
        if (!newValue || newValue.status !== 200) {
          loading.value = false;
          return;
        }
        const list = newValue.data?.results
          ? newValue.data.results
          : newValue.data || [];
        items.value = list.map((account) => ({
          ...account,
          loadingDeviceCode: false,
          loadingAccessToken: false,
          loadingRevoke: false,
        }));
        loading.value = false;

        // Accounts left mid-authorization keep being polled after a reload,
        // the same way the account card does it in the old admin panel.
        items.value.forEach(refreshAuthInfo);
      },
      { deep: true }
    );

    getData();

    return {
      items,
      loading,
      getDeviceFlow,
      openRevokeModal,
      revokeAccessToken,
      accountToRevoke,
      revokeModalContent,
      hasMissingSettings,
      canAddAccount,
      canAssociateAccount,
      canRevokeAccountToken,
      canViewAnySettings,
    };
  },
};
</script>

<style scoped>
/* Connection status badge. The spacing is set on the badge itself, because
   Font Awesome (dom.watch) swaps every <i class="fa ..."> for an <svg> that
   carries no scope attribute, so scoped rules cannot target the icon. */
.ibox-tools .label {
  align-items: center;
  display: inline-flex;
  gap: 4px;
}

.allegro-account-stats {
  align-items: flex-start;
  display: flex;
  flex-wrap: nowrap;
}

.allegro-account-stat {
  flex: 1 1 0;
  float: none;
}

.allegro-account-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 15px 10px;
}

.allegro-account-actions a {
  display: block;
  text-decoration: none;
}

.allegro-account-actions a:hover,
.allegro-account-actions a:focus {
  text-decoration: none;
}

.allegro-account-action {
  border-radius: 3px;
  font-size: 14px;
  font-weight: 400;
  padding: 7px 12px;
  text-align: center;
}

.allegro-account-action.btn-default {
  background: #fff;
  border: 1px solid #e5e6e7;
  color: #676a6c;
}

.allegro-account-action.btn-default:hover,
.allegro-account-action.btn-default:focus {
  background: #f9f9f9;
  border-color: #d2d2d2;
  color: #676a6c;
}

.allegro-account-connect {
  background-color: #f8ac59;
  border-color: #f8ac59;
  color: #fff;
}

.allegro-account-connect:hover,
.allegro-account-connect:focus {
  background-color: #f7a54a;
  border-color: #f7a54a;
  color: #fff;
}
</style>
